import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Eye, LayoutList, Scissors, Receipt, BarChart3,
  Map as MapIcon, CalendarDays, Tv, Settings, LogOut, ShoppingBag, Crown,
} from "lucide-react";
import { GlossLogomark } from "@/components/GlossLogomark";
import { SettingsModal } from "@/components/SettingsModal";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { useBranch } from "@/contexts/BranchContext";
import { PERSONA } from "@/constants/testIds";
import { api, unwrap } from "@/lib/api";
import "@/components/persona.css";

// ============================================================
// Persona catalog — gradient palettes match the spec
// ============================================================
const PERSONAS = [
  {
    key: "client", label: "Client Preview", route: "/client", icon: Eye,
    desc: "See what a client experiences via WhatsApp + walk-in.",
    grad: "linear-gradient(135deg,#F5E6D3,#EDD5C0)", iconFg: "#8E6A1A",
  },
  {
    key: "receptionist", label: "Receptionist", route: "/receptionist", icon: LayoutList,
    desc: "The front desk: queue, walk-ins, emergencies.",
    grad: "linear-gradient(135deg,#E8D5F5,#D5BEF0)", iconFg: "#5E4060",
    badge: "queue",
  },
  {
    key: "stylist", label: "Stylist / Therapist", route: "/stylist", icon: Scissors,
    desc: "Service notes, formulas, products used, history.",
    grad: "linear-gradient(135deg,#D5EDD5,#BEE0BE)", iconFg: "#15803D",
    badge: "waiting",
  },
  {
    key: "billing", label: "Billing", route: "/billing", icon: Receipt,
    desc: "Tip, gift card, split, discounts, WhatsApp receipt.",
    grad: "linear-gradient(135deg,#F5EDD5,#F0E0BE)", iconFg: "#8E6A1A",
  },
  {
    key: "owner", label: "Manager / Owner", route: "/owner", icon: BarChart3,
    desc: "KPIs, staff, revenue, churn, AI co-pilot.",
    grad: "linear-gradient(135deg,#D5E8F5,#BEDEF0)", iconFg: "#1E40AF",
    badge: "revenue",
  },
  {
    key: "floormap", label: "Floor Map", route: "/floormap", icon: MapIcon,
    desc: "Real-time chairs + rooms, drag-assign.",
    grad: "linear-gradient(135deg,#F5D5E8,#F0BED8)", iconFg: "#9D174D",
    badge: "chairs",
  },
  {
    key: "shifts", label: "Shift Manager", route: "/shifts", icon: CalendarDays,
    desc: "Weekly schedule, leaves, publish week.",
    grad: "linear-gradient(135deg,#DDD5F5,#CABEF0)", iconFg: "#5E4060",
  },
  {
    key: "products", label: "Product Sales", route: "/products", icon: ShoppingBag,
    desc: "Retail products sold by staff to clients.",
    grad: "linear-gradient(135deg,#F5DDD5,#F0CABE)", iconFg: "#8E6A1A",
  },
  {
    key: "memberships", label: "Memberships", route: "/memberships", icon: Crown,
    desc: "Silver · Gold · Platinum tiers and benefits.",
    grad: "linear-gradient(135deg,#F0D898,#D4A853)", iconFg: "#5E4060",
  },
  {
    key: "display", label: "Waiting Display", route: "/display", icon: Tv,
    desc: "Public TV — masked names, beauty tips.",
    grad: "linear-gradient(135deg,#1C1917,#3D2B1F)", iconFg: "#D4A853",
    public: true,
  },
];

const BG_IMG = "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=1400&q=80";

// ============================================================
// Number count-up hook
// ============================================================
function useCountUp(target, duration = 700) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!target) { setVal(0); return; }
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(target * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
      else setVal(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

// ============================================================
// Main page
// ============================================================
export default function PersonaSelector() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { settings } = useSettings();
  const { branches, activeBranchId, selectBranch } = useBranch();
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [staffCount, setStaffCount] = useState(0);
  const [bgFailed, setBgFailed] = useState(false);

  useEffect(() => {
    api.get("/stats/today").then((r) => setStats(unwrap(r) || null)).catch(() => {});
    api.get("/staff").then((r) => setStaffCount((unwrap(r) || []).length)).catch(() => {});
    // Preload bg image so we can swap to the gradient fallback if it fails
    const img = new Image();
    img.onerror = () => setBgFailed(true);
    img.src = BG_IMG;
  }, []);

  // Pre-navigate on mousedown for a snappier feel
  const fastEnter = (p) => (e) => {
    if (e.button !== 0) return;
    if (p.public) { window.open(p.route, "_blank"); return; }
    navigate(p.route);
  };

  const activeClients = useMemo(() => {
    if (!stats) return 0;
    return (stats.checked_in || 0) + (stats.in_service || 0) + (stats.booked || 0);
  }, [stats]);

  const bgStyle = bgFailed
    ? { background: "linear-gradient(135deg,#FAF0F0 0%,#F8E1DD 50%,#FFF7E4 100%)" }
    : { backgroundImage: `url(${BG_IMG})`, backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed", filter: "brightness(0.88) saturate(1.1)" };

  return (
    <div className="min-h-screen relative" data-testid={PERSONA.root}>
      {/* Layer 0 — photo background */}
      <div className="fixed inset-0 -z-30 ps-bg-fade" aria-hidden="true" style={bgStyle} />
      {/* Layer 1 — gradient tint */}
      <div className="fixed inset-0 -z-20 pointer-events-none" aria-hidden="true"
           style={{ background: "linear-gradient(135deg, rgba(250,240,240,0.72) 0%, rgba(124,92,122,0.15) 50%, rgba(212,168,83,0.12) 100%)" }} />
      {/* Layer 2 — radial light pools */}
      <div className="fixed inset-0 -z-10 pointer-events-none" aria-hidden="true"
           style={{ background: "radial-gradient(ellipse 50% 40% at 20% 20%, rgba(212,168,83,0.18) 0%, transparent 60%), radial-gradient(ellipse 40% 50% at 80% 80%, rgba(124,92,122,0.14) 0%, transparent 60%)" }} />

      {/* Top bar */}
      <header
        className="fixed top-0 left-0 right-0 z-40 ps-topbar-anim"
        style={{
          height: 68,
          background: "rgba(253,249,244,0.78)",
          backdropFilter: "blur(40px) saturate(160%)",
          WebkitBackdropFilter: "blur(40px) saturate(160%)",
          borderBottom: "1px solid rgba(255,255,255,0.70)",
        }}
      >
        <div className="h-full max-w-[1500px] mx-auto px-6 flex items-center gap-4">
          <button onClick={() => navigate("/login")} className="btn-3d btn-glass-3d btn-sm" aria-label="Back">
            <ArrowLeft size={16} />
          </button>
          <div className="flex items-center gap-2.5">
            <GlossLogomark size={32} animate={false} />
            <span className="font-display font-bold text-[18px] text-[var(--color-text-primary)] tracking-[-0.02em]">GLOSS</span>
          </div>

          <button onClick={() => setOpen(true)} className="ml-3 flex items-center gap-2 px-3 py-1.5 rounded-full glass-subtle hover:brightness-105 transition" data-testid={PERSONA.salonChip}>
            <Scissors size={14} className="text-[var(--color-mauve)]" />
            <span className="font-sora text-[13.5px] font-semibold">{settings.salon_name}</span>
            <span className="font-inter text-[12px] text-[var(--color-text-muted)]">·</span>
            <select
              value={activeBranchId}
              onChange={(e) => { e.stopPropagation(); selectBranch(e.target.value); }}
              onClick={(e) => e.stopPropagation()}
              className="bg-transparent font-inter text-[12px] text-[var(--color-text-secondary)] outline-none cursor-pointer"
            >
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <Settings size={13} className="text-[var(--color-text-muted)] ml-1" />
          </button>

          <div className="flex-1" />
          <button onClick={() => { logout(); navigate("/login"); }} className="w-10 h-10 rounded-full glass-subtle flex items-center justify-center" data-testid={PERSONA.logoutBtn} aria-label="Logout">
            <LogOut size={16} />
          </button>
        </div>
      </header>

      <main className="relative max-w-[1500px] mx-auto px-6" style={{ paddingTop: 116, paddingBottom: 80 }}>
        {/* Decorative SVG behind grid */}
        <DecorativeBackdrop />

        {/* Page header */}
        <section className="relative z-10 text-center" style={{ paddingTop: 16, paddingBottom: 40 }}>
          <div className="flex justify-center mb-5 ps-logomark">
            <GlossLogomark size={56} animate={false} />
          </div>
          <h1 className="font-display font-bold tracking-[-0.02em] text-[var(--color-text-primary)] ps-heading"
              style={{ fontSize: "clamp(32px, 5vw, 44px)" }}>
            Select your role
          </h1>
          <p className="font-inter text-[var(--color-text-muted)] mt-3 ps-sub" style={{ fontSize: "clamp(14px, 1.4vw, 16px)" }}>
            How would you like to enter the system today?
          </p>

          {/* Live status pill */}
          <div className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass-subtle ps-status" data-testid="persona-live-status">
            <span className="inline-flex relative w-2 h-2 rounded-full" style={{ background: "#22C55E" }}>
              <span className="absolute inset-0 rounded-full" style={{ background: "#22C55E", animation: "psPulseDot 1.6s ease-in-out infinite" }} />
            </span>
            <span className="font-inter text-[12.5px] text-[var(--color-text-secondary)]">
              Live ·&nbsp;
              <span className="font-mono font-medium text-[var(--color-text-primary)]"><CountNumber value={activeClients} /></span>
              &nbsp;active clients ·&nbsp;
              <span className="font-mono font-medium text-[var(--color-text-primary)]"><CountNumber value={staffCount} /></span>
              &nbsp;staff on shift
            </span>
          </div>
        </section>

        {/* Persona grid */}
        <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 max-w-[1080px] mx-auto">
          {PERSONAS.map((p, i) => (
            <PersonaCard key={p.key} persona={p} idx={i} stats={stats} onEnter={fastEnter(p)} />
          ))}
        </div>

        {/* Footer */}
        <footer className="mt-14 flex flex-col items-center gap-3 text-center">
          <p className="font-inter text-[13px]" style={{ color: "#A8A29E" }}>
            Switch between roles anytime · Click the salon chip above to personalise
          </p>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass-subtle">
            <GlossLogomark size={20} animate={false} />
            <span className="font-sora font-medium text-[12px] text-[var(--color-text-secondary)]">
              GLOSS · {settings.salon_name} · {settings.branch_name}
            </span>
          </div>
        </footer>
      </main>

      {open && <SettingsModal onClose={() => setOpen(false)} />}
    </div>
  );
}

// ============================================================
// Number with count-up
// ============================================================
function CountNumber({ value }) {
  const v = useCountUp(value, 600);
  return <>{Math.round(v).toLocaleString("en-IN")}</>;
}
function MoneyCount({ value }) {
  const v = useCountUp(value, 700);
  return <>₹{Math.round(v).toLocaleString("en-IN")}</>;
}

// ============================================================
// Persona Card
// ============================================================
function PersonaCard({ persona: p, idx, stats, onEnter }) {
  const Icon = p.icon;
  const isDark = p.key === "display";

  const badge = (() => {
    if (!stats) return null;
    if (p.badge === "queue") {
      const n = (stats.checked_in || 0) + (stats.booked || 0);
      if (n === 0) return null;
      return { text: <><CountNumber value={n} />&nbsp;in queue</>,
        bg: "rgba(245,158,11,0.12)", fg: "#B45309", border: "rgba(245,158,11,0.30)", dot: "#F59E0B" };
    }
    if (p.badge === "revenue") {
      const n = stats.revenue_today || 0;
      if (n === 0) return null;
      return { text: <><MoneyCount value={n} />&nbsp;today</>,
        bg: "rgba(212,168,83,0.12)", fg: "#B5892E", border: "rgba(212,168,83,0.30)", dot: "#D4A853" };
    }
    if (p.badge === "chairs") {
      const n = stats.in_service || 0;
      if (n === 0) return null;
      return { text: <><CountNumber value={n} />&nbsp;{n === 1 ? "chair" : "chairs"} busy</>,
        bg: "rgba(22,163,74,0.10)", fg: "#15803D", border: "rgba(22,163,74,0.28)", dot: "#22C55E" };
    }
    if (p.badge === "waiting") {
      const n = stats.checked_in || 0;
      if (n === 0) return null;
      return { text: <><CountNumber value={n} />&nbsp;clients waiting</>,
        bg: "rgba(124,92,122,0.10)", fg: "#5E4060", border: "rgba(124,92,122,0.26)", dot: "#7C5C7A" };
    }
    return null;
  })();

  // 60ms staggered slideUp on mount (post-hero entry)
  const delay = 520 + idx * 60;

  return (
    <button
      onMouseDown={onEnter}
      onClick={(e) => e.preventDefault()}
      data-testid={PERSONA.card(p.key)}
      className={`ps-card group text-left relative overflow-hidden ${isDark ? "ps-card-dark" : ""}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <span className="ps-shimmer" aria-hidden="true" />

      <div className="relative z-[1] flex items-start justify-between gap-3">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center ps-icon-box"
             style={{ background: p.grad }}>
          <Icon size={26} style={{ color: p.iconFg }} />
        </div>
        <span
          className="ps-enter-link inline-flex items-center gap-1 font-sora font-semibold text-[12.5px]"
          style={{ color: isDark ? "#D4A853" : p.iconFg }}
          data-testid={PERSONA.enterBtn(p.key)}
        >
          {p.public ? "Launch" : "Enter"} <ArrowRight size={13} />
        </span>
      </div>

      <div className="relative z-[1] mt-4">
        <h3 className="font-sora font-semibold leading-tight"
            style={{ fontSize: 19, color: isDark ? "white" : "#1C1917" }}>
          {p.label}
        </h3>
        <p className="font-inter mt-1.5 leading-snug"
           style={{ fontSize: 13, color: isDark ? "rgba(255,255,255,0.65)" : "#78716C" }}>
          {p.desc}
        </p>

        {/* Live status badge — inline so it never overlaps wrapped description */}
        {badge && (
          <span
            className="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 rounded-full font-inter font-medium text-[11.5px]"
            style={{ background: badge.bg, color: badge.fg, border: `1px solid ${badge.border}` }}
            data-testid={`persona-live-badge-${p.key}`}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: badge.dot, animation: "psPulseDot 1.6s ease-in-out infinite" }} />
            {badge.text}
          </span>
        )}

        {/* Public chip — inline so it never overlaps the description */}
        {p.public && (
          <span
            className="inline-flex items-center gap-1 mt-3 text-[10px] font-sora font-semibold tracking-[0.18em] uppercase px-2 py-1 rounded-full"
            style={{ background: "rgba(212,168,83,0.20)", color: "#D4A853", border: "1px solid rgba(212,168,83,0.30)" }}
          >
            Public · Opens in new tab
          </span>
        )}
      </div>
    </button>
  );
}

// ============================================================
// Decorative SVG backdrop
// ============================================================
function DecorativeBackdrop() {
  return (
    <svg
      aria-hidden="true"
      className="hidden md:block pointer-events-none absolute"
      style={{ top: 80, left: "50%", transform: "translateX(-50%)", opacity: 0.05, zIndex: 0 }}
      width="1100" height="800" viewBox="0 0 1100 800" fill="none"
    >
      <circle cx="320" cy="380" r="240" stroke="#D4A853" strokeWidth="1" />
      <circle cx="780" cy="420" r="280" stroke="#D4A853" strokeWidth="1" />
      {/* GLOSS logomark — large outline diamond */}
      <g transform="translate(550 400) rotate(45)">
        <rect x="-180" y="-180" width="360" height="360" stroke="#5E4060" strokeWidth="1" fill="none" />
        <rect x="-110" y="-110" width="220" height="220" stroke="#5E4060" strokeWidth="1" fill="none" />
      </g>
      {/* Abstract scissors silhouette */}
      <g transform="translate(880 130)" stroke="#5E4060" strokeWidth="1" fill="none">
        <circle cx="0" cy="0" r="22" />
        <circle cx="0" cy="40" r="22" />
        <path d="M 14 14 L 100 100 M 14 28 L 100 -58" />
      </g>
    </svg>
  );
}
